import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.scss';

import { useLocation } from "react-router-dom";

import { ToastsProvider, useToasts } from './contexts/toasts';
import { FullScreenLoaderProvider } from './contexts/fullScreenLoader';

import { AuthProvider, useAuth } from './contexts/auth';
import Cookies from 'js-cookie';
import { Notifications } from './api/Notifications';
import { EventEmitter } from './utils/EventEmitter';
import { renderNotificationMessage, renderNotificationTitle } from './components/list-items/NotificationItem';
import AppRouter from './routing/AppRouter';
import { useDispatch, useSelector } from 'react-redux';
import { sectionLabelsSelector, updateSections } from './slices/sectionsSlice';
import { ControlPanelManager } from './api/ControlPanelManager';
import { NavigationSection, SectionKey } from './components/layout/AppSidebar';
import { RootState } from './store';
import { AppNotification } from './models/Notification';


function App() {
  const { pathname } = useLocation();
  const [source, setSource] = useState<EventSource>();
  const sourceRef = useRef<EventSource>();
  const dispatch = useDispatch();

  const { token, setLoggedUser, setNewNotifications } = useAuth();
  const { showToast } = useToasts();

  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  useEffect(() => {
    sourceRef.current = source;
  }, [source]);

  useEffect(() => {
    if (token) {
      Notifications.newNotificationsCount()
        .then(
          ({ data }) => setNewNotifications(data.count),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast('notifications-error', message, `Notifications Error ${status ? `(${status})` : ''}`, 'danger');
          }
        );

      const params = new URLSearchParams([['raw', 'true']]);
      ControlPanelManager.getSections(params)
        .then(
          ({ data }) => {
            window.__env.NAVIGATION_SECTIONS = data as Record<SectionKey, NavigationSection>;
            dispatch(updateSections(data));
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            console.warn('Error while retrieving sections', status, message, 'Fallback to default sections');
          }
        )

      const userString = Cookies.get(window.__env.AUTH_USER_NAME || "alida-user");
      if (userString) setLoggedUser(JSON.parse(userString));
    }
  }, [token, setLoggedUser, setNewNotifications, showToast, dispatch]);

  const initListening = useCallback(() => {
    const eventSource = Notifications.listenNotifications(
      (evt) => {
        if (evt.lastEventId === "heartbeat" || !evt.data) return;

        try {
          const notification: AppNotification = JSON.parse(evt.data);
          EventEmitter.dispatch(notification.type, notification);
          if (notification.type !== "JOB_PROGRESS" && (notification.type !== "BDA_APP_STATUS_CHANGE" || !notification.details.serviceId)) {
            EventEmitter.dispatch("new-notification", notification);
            showToast(`notification-${notification.id}`, renderNotificationMessage(notification, applicationLabels.singular), renderNotificationTitle(notification, applicationLabels.singular), "info")
            setNewNotifications(count => count ? count + 1 : undefined);
          }
        }
        catch (e) {
          console.log("Got error for message", e);
        }
      },
      (_evt) => {
        if (sourceRef.current) sourceRef.current.close();
        setSource(undefined);
      }
    );
    setSource(eventSource);
  }, [setNewNotifications, showToast, applicationLabels]);

  useEffect(() => {
    if (pathname !== "/login" && (!sourceRef.current || sourceRef.current.readyState === 2) && token) initListening();
    else if (pathname === "/login") Notifications.stopListening(sourceRef.current!);
  }, [pathname, token, initListening]);

  return <AppRouter />;
};

export default function withContext() {
  return (
    <FullScreenLoaderProvider>
      <ToastsProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ToastsProvider>
    </FullScreenLoaderProvider>
  )
}
